import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCartItems,
  selectCartRestaurantDetails,
  selectItems,
} from "../../redux/features/restaurants/restaurantSelectors";
import {
  setCartItems,
  setCartRestaurantDetails,
  setCartSubTotal,
  setShowProductDetail,
  addVariantItem,
  removeVariantItem,
  setEditingVariantItem,
} from "../../redux/features/restaurants/restaurantSlice";
import { updateSubtotal } from "../../utils/menu";
import VariantDropdown from "./variantDropdown";
import MenuItemCard from "./menuItemCard";
import { getItemDetailsAsync } from "../../redux/features/restaurants/restaurantThunks";

const MenuItemsList = ({ activeCategory, searchQuery, branchDetails }) => {
  const menuItemsList = useSelector(selectItems);
  const cartItems = useSelector(selectCartItems);
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const [loadingItemId, setLoadingItemId] = useState(null);
  const dispatch = useDispatch();
  // Helper function to format price with KWD currency prefix
  const formatPrice = (price) => {
    // If price already includes KWD, return as is
    if (typeof price === "string" && price.startsWith("KWD ")) {
      return price;
    }

    // Convert to number and format with 3 decimal places, then add KWD prefix
    const numericPrice = parseFloat(price);
    return `KWD ${numericPrice.toFixed(3)}`;
  };

  // Get non-variant cart items
  const nonVariantCartItems = cartItems.filter((item) => !item.has_variants);

  // Memoized function to get item quantity from Redux cart
  const getItemQuantity = useCallback(
    (itemId) => {
      const cartItem = cartItems.find(
        (item) => item.id === itemId && !item.has_variants
      );
      return cartItem ? cartItem.quantity : 0;
    },
    [cartItems]
  );

  // Warning modal to be continued
  const checkExistingCartItems = () => {
    if (cartRestaurantDetails.restaurantName === branchDetails.restaurantName) {
      return true;
    }
    return false;
  };

  // Add item to cart (both for new and existing items)
  const addItem = (itemToAdd) => {
    if (cartRestaurantDetails && cartItems.length !== 0) {
      checkExistingCartItems();
    }

    // Look for existing item in cart by ID (avoiding has_variants mismatch)
    const existingItem = cartItems.find(
      (cartItem) => cartItem.id === itemToAdd.id && !cartItem.has_variants
    );

    let updatedItem;
    if (existingItem) {
      // Update existing item (keep all properties intact, just update quantity)
      updatedItem = {
        ...existingItem,
        quantity: existingItem.quantity + 1,
      };
    } else {
      // Create new item (ensure price is formatted)
      updatedItem = {
        ...itemToAdd,
        quantity: 1,
        price: formatPrice(itemToAdd.price),
      };
    }

    // Dispatch updated item to Redux
    dispatch(setCartItems(updatedItem));

    // Calculate subtotal directly from entire updated cart
    const updatedCart = [
      ...cartItems.filter(
        (cartItem) => cartItem.id !== itemToAdd.id || cartItem.has_variants
      ),
      updatedItem,
    ];
    dispatch(setCartSubTotal(updateSubtotal(updatedCart)));
  };

  // Remove item from cart
  const removeItem = (itemToRemove) => {
    // Find the exact item in cart (avoiding has_variants mismatch)
    const existingItem = cartItems.find(
      (cartItem) => cartItem.id === itemToRemove.id && !cartItem.has_variants
    );

    if (!existingItem) return;

    // Decrement quantity (maintaining all other properties)
    const updatedItem = {
      ...existingItem,
      quantity: existingItem.quantity - 1,
    };

    // Dispatch the updated item
    dispatch(setCartItems(updatedItem));

    // Calculate updated subtotal for the entire cart
    let updatedCart;
    if (updatedItem.quantity <= 0) {
      updatedCart = cartItems.filter(
        (cartItem) => cartItem.id !== itemToRemove.id || cartItem.has_variants
      );
    } else {
      updatedCart = cartItems.map((cartItem) =>
        cartItem.id === itemToRemove.id && !cartItem.has_variants
          ? updatedItem
          : cartItem
      );
    }

    dispatch(setCartSubTotal(updateSubtotal(updatedCart)));
  };

  const handleVariantItems = (item, event) => {
    if (event === "add") {
      dispatch(setEditingVariantItem(null));

      setLoadingItemId(item.id); // Set loading state
      dispatch(
        getItemDetailsAsync(item.restaurant_id, item.branch_id, item.id)
      ).finally(() => setLoadingItemId(null)); // Reset loading state after request
    }
  };

  const handleNonVariantItems = (item, event) => {
    dispatch(setShowProductDetail(false));

    // We need to operate on the original item from menuItemsList
    // Not the displayItem which might have outdated quantity from previous render
    const menuItem = menuItemsList.find((menuItem) => menuItem.id === item.id);

    if (!menuItem) return;

    if (event === "add") {
      addItem(menuItem);
    } else if (event === "remove") {
      removeItem(menuItem);
    }
  };

  const handleSelectedItem = (item, event) => {
    if (item.has_variants && item.variants.length !== 0) {
      handleVariantItems(item, event);
    } else {
      handleNonVariantItems(item, event);
    }
  };

  const removeSubItem = (item, variantItem) => {
    // Dispatch the variant removal action
    dispatch(
      removeVariantItem({
        productId: item.id,
        variantId: variantItem.variantId,
      })
    );
  };

  const addSubItem = (item, variantItem) => {
    // Dispatch the variant add action
    dispatch(
      addVariantItem({
        productId: item.id,
        variantId: variantItem.variantId,
      })
    );
  };

  const cardActiveHandler = (itemId, has_variants) => {
    if (has_variants) {
      const isVariantInCart = cartItems.some(
        (item) => item.id === itemId && item.has_variants
      );
      return isVariantInCart;
    } else {
      const isItemInCart = cartItems.some(
        (item) => item.id === itemId && !item.has_variants
      );
      return isItemInCart;
    }
  };

  useEffect(() => {
    if (!cartRestaurantDetails.restaurantId || branchDetails.restaurant_id !== cartRestaurantDetails.restaurantId) {
      dispatch(setCartRestaurantDetails(branchDetails));
    }
  }, [cartRestaurantDetails, branchDetails, dispatch]);

  return (
    <div className="w-full h-full">
      <div className="flex flex-wrap sm:justify-start justify-center gap-4 mt-4 h-full overflow-y-auto pb-8">
        {menuItemsList
          ?.filter(
            (item) =>
              (activeCategory === "All Menu" ||
                item.category_id === activeCategory) &&
              (item.name_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description_en
                  ?.toLowerCase()
                  .includes(searchQuery.toLowerCase()))
          )
          .sort((a, b) => {
            // Only sort when All Menu is selected
            if (activeCategory === "All Menu") {
              return a.category_id - b.category_id;
            }
            return 0;
          })
          .map((item) => {
            // Get current quantity from Redux cart
            const currentQuantity = getItemQuantity(item.id);

            // Format the price to include KWD prefix for display
            const displayItem = {
              ...item,
              price: formatPrice(item.price),
              quantity: currentQuantity,
            };

            return (
              <div
                key={item.id}
                className={`border-2 p-2 rounded-xl shadow-md flex flex-col gap-4 relative w-[249px] h-36 ${
                  cardActiveHandler(item.id, item.has_variants)
                    ? "border-amber-100"
                    : "border-gray-150"
                } relative transition-all duration-300 ease-in-out`}
                style={{
                  marginBottom: (() => {
                    // Find variant items for this product
                    const variantItems = cartItems.find(
                      (variantItem) =>
                        variantItem.id === item.id && variantItem?.has_variants
                    );
                    
                    // If variant items exist, calculate dynamic margin based on number of items
                    if (variantItems) {
                      // Base margin of 50px (approx. the same as mb-20) 
                      // Plus 25px for each additional variant item beyond the first one
                      const itemsCount = variantItems.items?.length || 0;
                      return itemsCount > 0 ? 50 + (itemsCount - 1) * 25 : 0;
                    }
                    
                    return 0;
                  })() + 'px'
                }}
              >
                <MenuItemCard
                  orderedItems={cartItems}
                  item={displayItem}
                  handleSelectedItem={handleSelectedItem}
                  loadingItemId={loadingItemId}
                />
                {cartItems.find(
                  (variantItem) =>
                    variantItem.id === item.id && variantItem.has_variants
                ) && (
                  <VariantDropdown
                    item={cartItems.filter(
                      (variantItem) =>
                        variantItem.id === item.id && variantItem?.has_variants
                    )}
                    removeSubItem={removeSubItem}
                    addSubItem={addSubItem}
                    branchDetails={branchDetails}
                    isCatering={false}
                  />
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default MenuItemsList;
